<template>
    <div
        class="flex flex-col max-w-screen-xl mx-auto px-4 sm:px-6 md:px-8 lg:px-32">
        <!-- Header -->
        <div
            class="text-lg sm:text-xl md:text-4xl text-san-marino-600 font-semibold mt-8 text-center px-8 sm:px-36 md:px-16 lg:px-20 xl:px-64">
            <h1>Nereye seyahat ederseniz edin Kolay eSIM ile bağlı kalın</h1>
        </div>
        <div
            class="text-lg sm:text-xl md:text-3xl text-san-marino-800 font-semibold mt-2 text-center px-4 mb-8">
            <h2>Uygun fiyatlarla kesintisiz internet erişimi</h2>
        </div>
        <!-- Packages Tab List -->
        <div class="card">
            <Tabs value="0">
                <TabList pt:tabList="justify-center">
                    <Tab value="0" pt:root="px-2 sm:px-4"
                        >Yerel eSIM'ler</Tab
                    >
                    <Tab value="1" pt:root="px-2 sm:px-4"
                        >Bölgesel eSIM'ler</Tab
                    >
                    <Tab value="2" pt:root="px-2 sm:px-4"
                        >Global eSIM'ler</Tab
                    >
                </TabList>
                <TabPanels pt:root="px-0">
                    <TabPanel value="0">
                        <!-- Popular Countries -->
                        <div class="flex flex-col items-center">
                            <div
                                class="flex flex-wrap w-full gap-3 sm:gap-4 md:gap-5 lg:gap-6 xl:gap-8 justify-center my-8">
                                <div
                                    v-ripple
                                    v-for="country in allCountries"
                                    class="flex w-full sm:w-[calc(50%-8px)] md:w-[calc(33.33%-12px)] lg:w-60 xl:w-80 h-14 sm:h-16 bg-san-marino-100 drop-shadow px-3 sm:px-4">
                                    <NuxtLink
                                        class="flex items-center gap-x-2 w-full"
                                        :to="`/${country.slug}`">
                                        <span
                                            class="!w-8 sm:!w-12 h-6 sm:h-8 fi drop-shadow"
                                            :class="`fi-${country.code.toLowerCase()}`"></span>
                                        <span
                                            class="text-san-marino-700 text-lg sm:text-xl font-medium flex-1 truncate"
                                            >{{ country.name }}</span
                                        >
                                        <span
                                            class="pi pi-chevron-right text-san-marino-700"></span>
                                    </NuxtLink>
                                </div>
                            </div>
                        </div>
                    </TabPanel>
                    <TabPanel value="1">
                        <!-- Regions -->
                        <div class="flex flex-col items-center">
                            <div
                                class="flex flex-wrap w-full gap-3 sm:gap-4 md:gap-5 lg:gap-6 xl:gap-8 justify-center my-8">
                                <div
                                    v-ripple
                                    v-for="region in regions"
                                    class="flex w-full sm:w-[calc(50%-8px)] md:w-[calc(33.33%-12px)] lg:w-60 xl:w-80 h-14 sm:h-16 bg-san-marino-100 drop-shadow px-3 sm:px-4">
                                    <NuxtLink
                                        class="flex items-center gap-x-2 w-full"
                                        :to="`/${region.slug}`">
                                        <span
                                            class="text-san-marino-700 text-lg sm:text-xl font-medium flex-1 truncate"
                                            >{{ region.name }}</span
                                        >
                                        <span
                                            class="pi pi-chevron-right text-san-marino-700"></span>
                                    </NuxtLink>
                                </div>
                            </div>
                        </div>
                    </TabPanel>
                    <TabPanel value="2">
                        <!-- Global -->
                        <div class="flex flex-col items-center">
                            <div
                                class="flex flex-wrap w-full gap-3 sm:gap-4 md:gap-5 lg:gap-6 xl:gap-8 justify-center my-8">
                                <div
                                    v-ripple
                                    class="flex w-full sm:w-[calc(50%-8px)] md:w-[calc(33.33%-12px)] lg:w-60 xl:w-80 h-14 sm:h-16 bg-san-marino-100 drop-shadow px-3 sm:px-4">
                                    <NuxtLink
                                        class="flex items-center gap-x-2 w-full"
                                        :to="`/world-esim`">
                                        <span
                                            class="text-san-marino-700 text-lg sm:text-xl font-medium flex-1 truncate"
                                            >{{ countries.find((c) => c.code === 'world').name }}</span
                                        >
                                        <span
                                            class="pi pi-chevron-right text-san-marino-700"></span>
                                    </NuxtLink>
                                </div>
                            </div>
                        </div>
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </div>
        <!-- How to works -->
        <HowToWorks />
        <!-- Compatible Devices -->
        <CompatibleDevices />
        <!-- Frequently Asked Questions -->
        <FrequentlyAskedQuestions />
    </div>
</template>

<script setup>
import { ref } from "vue";

useHead({
    title: "Uluslararası seyahatler için eSIM satın alın",
    meta: [
        {
            name: "description",
            content:
                "200'den fazla seyahat noktasında kesintisiz ve hızlı internet için uygun fiyatlarla sunulan seyahat eSIM'lerini bulun. Kolay eSIM ile bağlantıda kalın.",
        },
        {
            name: "keywords",
            content:
                "eSIM, eSIM alma, bölgesel eSIM, yerel eSIM, bölgesel eSIM alma, yerel eSIM alma, yerel eSIM satın alma, bölgesel eSIM satın alma, veri eSIM, e-SIM, eSIM planı, eSIM mağazası, eSIM satış yeri, seyahat eden, gezgin, veri planı, e-SIM alma, ucuz veri, bağlantı",
        },
    ],
});

const client = useSanctumClient();

const {
    data: countries,
    pending,
    error,
    refresh,
} = await useAsyncData("countries", () => client("/countries"));

const allCountries = ref([]);
const regions = ref([]);

allCountries.value = countries.value.filter((country) => country.area === "local");

regions.value = countries.value
    .filter((country) => country.area === "global")
    .filter((country) => country.code !== "world");
</script>
